import React, { useState, useEffect } from 'react';
import { 
  FileText, 
  Download, 
  Calendar as CalendarIcon,
  ChevronDown,
  Printer,
  Phone,
  Mail,
  MoreVertical,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';
import { supabase } from '../supabaseClient';
import { useNotifications } from '../context/NotificationContext';
import { useNavigate } from 'react-router-dom';
import StatusDropdown from '../components/StatusDropdown';
import VerificationModal from '../components/VerificationModal';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import './Reports.css';

const Reports = () => {
  const navigate = useNavigate();
  const { addNotification } = useNotifications();
  
  const activeStaff = JSON.parse(localStorage.getItem('gdt_active_staff') || '{}');
  const staffName = activeStaff.full_name || '';
  const isAdmin = activeStaff?.type === 'Administrador' || activeStaff?.type === 'Secretaria/o';

  const [activeTab, setActiveTab] = useState('diario');
  const [reportData, setReportData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedProf, setSelectedProf] = useState(isAdmin ? 'VER TODOS' : (staffName || 'VER TODOS'));
  const [professionals, setProfessionals] = useState([]);

  // Verification modal states
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [selectedAppt, setSelectedAppt] = useState(null);

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase.from('professionals').select('*').order('full_name', { ascending: true });
      if (error) throw error;
      setProfessionals(data || []);
    } catch (err) {
      console.error("Error fetching professionals for Reports:", err);
    }
  };

  const reportButtons = [
    { id: 'diario', label: 'LISTADO DIARIO' },
    { id: 'otorgados', label: 'TURNOS OTORGADOS' },
    { id: 'cancelados', label: 'TURNOS CANCELADOS' },
    { id: 'borrados', label: 'TURNOS BORRADOS' },
    { id: 'pacientes-borrados', label: 'PACIENTES BORRADOS' },
    { id: 'caja-borrados', label: 'REG. CAJA BORRADOS' },
    { id: 'espera', label: 'SALA DE ESPERA' },
    { id: 'ausentes', label: 'PACIENTES AUSENTES' },
    { id: 'informes', label: 'INFORMES' },
    { id: 'informes-borrados', label: 'INFORMES BORRADOS' },
    { id: 'graficos', label: 'ANALÍTICAS (GRÁFICOS)' },
  ];

  const fetchReportData = async () => {
    setLoading(true);
    setError(null);
    try {
      if (['diario', 'otorgados', 'cancelados', 'ausentes', 'espera', 'informes', 'graficos'].includes(activeTab)) {
        let query = supabase.from('appointments').select('*');
        
        if (selectedDate) query = query.eq('appointment_date', selectedDate);
        
        if (selectedProf && selectedProf !== 'VER TODOS') {
          query = query.eq('professional_name', selectedProf);
        }

        if (activeTab === 'cancelados') {
          query = query.eq('status', 'cancelado');
        } else if (activeTab === 'ausentes') {
          query = query.eq('status', 'ausente');
        } else if (activeTab === 'otorgados') {
          query = query.eq('status', 'confirmado');
        } else if (activeTab === 'espera') {
          query = query.in('status', ['presente', 'en-llamado', 'en-consultorio', 'atendido']);
        }

        const { data, error: dbError } = await query.order('appointment_time', { ascending: true });
        if (dbError) throw dbError;

        let result = data || [];
        if (activeTab === 'informes') {
          result = result.filter(app => app.study_details?.attached_files?.length > 0);
        }
        
        setReportData(result);

      } else if (['borrados', 'pacientes-borrados', 'caja-borrados', 'informes-borrados'].includes(activeTab)) {
        let query = supabase.from('audit_logs').select('*');
        
        if (activeTab === 'borrados') {
          query = query.eq('action', 'BORRADO TURNO');
        } else if (activeTab === 'pacientes-borrados') {
          query = query.eq('action', 'BORRADO PACIENTE');
        } else if (activeTab === 'caja-borrados') {
          query = query.eq('action', 'BORRADO CAJA');
        } else if (activeTab === 'informes-borrados') {
          query = query.eq('action', 'BORRADO TURNO');
        }

        if (selectedDate) {
          if (activeTab !== 'borrados' && activeTab !== 'informes-borrados') {
            query = query.gte('created_at', selectedDate + 'T00:00:00').lte('created_at', selectedDate + 'T23:59:59');
          }
        }

        const { data, error: dbError } = await query.order('created_at', { ascending: true });
        if (dbError) throw dbError;

        let filteredLogs = data || [];

        if (selectedDate && (activeTab === 'borrados' || activeTab === 'informes-borrados')) {
          filteredLogs = filteredLogs.filter(log => log.details?.appointment_date === selectedDate);
        }
        if (selectedProf && selectedProf !== 'VER TODOS' && (activeTab === 'borrados' || activeTab === 'informes-borrados')) {
          filteredLogs = filteredLogs.filter(log => log.details?.professional_name === selectedProf);
        }

        let result = [];
        if (activeTab === 'borrados') {
          result = filteredLogs.map(log => ({
            id: log.id,
            appointment_date: log.details?.appointment_date || '---',
            appointment_time: log.details?.appointment_time || '00:00:00',
            patient_name: log.details?.patient_name || '---',
            patient_dni: log.details?.patient_dni || '---',
            patient_phone: log.details?.patient_phone || '',
            patient_email: log.details?.patient_email || '',
            study_type: log.details?.study_type || 'Consulta General',
            professional_name: log.details?.professional_name || '---',
            deleted_at: new Date(log.created_at).toLocaleString('es-AR'),
            deleted_by: log.user_email || '---',
            notes: log.details?.admin_notes || '---'
          }));
        } else if (activeTab === 'informes-borrados') {
          result = filteredLogs
            .filter(log => log.details?.study_details?.attached_files?.length > 0)
            .map(log => ({
              id: log.id,
              appointment_date: log.details?.appointment_date || '---',
              appointment_time: log.details?.appointment_time || '00:00:00',
              patient_name: log.details?.patient_name || '---',
              patient_dni: log.details?.patient_dni || '---',
              patient_phone: log.details?.patient_phone || '',
              patient_email: log.details?.patient_email || '',
              study_type: log.details?.study_type || 'Consulta General',
              professional_name: log.details?.professional_name || '---',
              deleted_at: new Date(log.created_at).toLocaleString('es-AR'),
              deleted_by: log.user_email || '---',
              notes: 'Informe adjunto eliminado'
            }));
        } else if (activeTab === 'pacientes-borrados') {
          result = filteredLogs.map(log => ({
            id: log.id,
            patient_name: log.details?.full_name || '---',
            patient_dni: log.details?.dni || '---',
            patient_phone: log.details?.phone || '---',
            patient_email: log.details?.email || '---',
            insurance: log.details?.insurance_provider || '---',
            deleted_at: new Date(log.created_at).toLocaleString('es-AR'),
            deleted_by: log.user_email || '---'
          }));
        } else if (activeTab === 'caja-borrados') {
          result = filteredLogs.map(log => ({
            id: log.id,
            description: log.details?.description || '---',
            amount: log.details?.amount || '0',
            type: log.details?.type || '---',
            category: log.details?.category || '---',
            deleted_at: new Date(log.created_at).toLocaleString('es-AR'),
            deleted_by: log.user_email || '---'
          }));
        }

        setReportData(result);
      }
    } catch (err) {
      console.error('Error fetching reports:', err);
      setError(err.message || 'Error desconocido al cargar datos');
      addNotification('Error al cargar listados', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchConfig();
    });
  }, []);

  useEffect(() => {
    Promise.resolve().then(() => {
      fetchReportData();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, selectedDate, selectedProf]);

  const getPieData = () => {
    const counts = {};
    reportData.forEach(app => {
      counts[app.status] = (counts[app.status] || 0) + 1;
    });
    return Object.keys(counts).map(k => ({ name: k.toUpperCase(), value: counts[k] }));
  };
  
  const getProfData = () => {
    const counts = {};
    reportData.forEach(app => {
      counts[app.professional_name || 'Sin Asignar'] = (counts[app.professional_name || 'Sin Asignar'] || 0) + 1;
    });
    return Object.keys(counts).map(k => ({ name: k, turnos: counts[k] }));
  };

  const handleExportExcel = () => {
    if (!reportData || reportData.length === 0) {
      addNotification('No hay datos en el listado para exportar.', 'warning');
      return;
    }

    try {
      const isAuditTab = ['borrados', 'pacientes-borrados', 'caja-borrados', 'informes-borrados'].includes(activeTab);

      let headers = [];
      let rows = [];

      if (isAuditTab) {
        headers = ['Fecha Eliminación', 'Detalle / Nombre', 'DNI / Identificador', 'Motivo / Categoría', 'Eliminado por'];
        rows = reportData.map(item => [
          `"${item.deleted_at || ''}"`,
          `"${(item.patient_name || item.details?.full_name || '').replace(/"/g, '""')}"`,
          `"${item.patient_dni || item.details?.dni || ''}"`,
          `"${(item.reason || item.category || '').replace(/"/g, '""')}"`,
          `"${(item.deleted_by || '').replace(/"/g, '""')}"`
        ]);
      } else {
        headers = ['Fecha', 'Hora', 'Paciente', 'DNI', 'Teléfono', 'Email', 'Profesional', 'Estudio / Especialidad', 'Obra Social', 'Plan', 'Estado', 'Notas / Observaciones'];
        rows = reportData.map(item => [
          item.appointment_date || '',
          (item.appointment_time || '').substring(0, 5),
          `"${(item.patient_name || '').replace(/"/g, '""')}"`,
          `"${item.patient_dni || item.study_details?.patient_dni || ''}"`,
          `"${item.patient_phone || ''}"`,
          `"${item.patient_email || ''}"`,
          `"${(item.professional_name || '').replace(/"/g, '""')}"`,
          `"${(item.study_type || '').replace(/"/g, '""')}"`,
          `"${(item.insurance_provider || item.study_details?.insurance_provider || '').replace(/"/g, '""')}"`,
          `"${(item.insurance_plan || item.study_details?.insurance_plan || '').replace(/"/g, '""')}"`,
          `"${(item.status || '').toUpperCase()}"`,
          `"${(item.admin_notes || item.notes || '').replace(/"/g, '""')}"`
        ]);
      }

      const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(r => r.join(';'))].join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      const tabName = reportButtons.find(b => b.id === activeTab)?.label || activeTab;
      const cleanTabName = tabName.replace(/[^a-zA-Z0-9_-]/g, '_');
      const filename = `Reporte_${cleanTabName}_${selectedDate || 'general'}.csv`;
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification(`Listado exportado a Excel (${reportData.length} registros).`, 'success');
    } catch (err) {
      console.error('Error exporting report to Excel:', err);
      addNotification('Error al generar el archivo Excel: ' + err.message, 'error');
    }
  };

  const handlePrintList = () => {
    if (!reportData || reportData.length === 0) {
      addNotification('No hay datos en el listado para imprimir.', 'warning');
      return;
    }

    try {
      const isAuditTab = ['borrados', 'pacientes-borrados', 'caja-borrados', 'informes-borrados'].includes(activeTab);
      const tabName = reportButtons.find(b => b.id === activeTab)?.label || activeTab;

      const printWindow = window.open('', '_blank', 'width=1100,height=850');
      if (!printWindow) {
        addNotification('Por favor permite las ventanas emergentes (popups) para imprimir el listado.', 'warning');
        return;
      }

      let tableHeadersHtml = '';
      let tableRowsHtml = '';

      if (isAuditTab) {
        tableHeadersHtml = `
          <tr>
            <th>Fecha Eliminación</th>
            <th>Detalle / Nombre</th>
            <th>DNI / ID</th>
            <th>Motivo / Categoría</th>
            <th>Eliminado Por</th>
          </tr>
        `;
        tableRowsHtml = reportData.map(item => `
          <tr>
            <td>${item.deleted_at || ''}</td>
            <td><strong>${item.patient_name || item.details?.full_name || '---'}</strong></td>
            <td>${item.patient_dni || item.details?.dni || '---'}</td>
            <td>${item.reason || item.category || '---'}</td>
            <td>${item.deleted_by || '---'}</td>
          </tr>
        `).join('');
      } else {
        tableHeadersHtml = `
          <tr>
            <th>Fecha</th>
            <th>Hora</th>
            <th>Paciente / DNI</th>
            <th>Contacto</th>
            <th>Profesional</th>
            <th>Estudio / Especialidad</th>
            <th>Obra Social / Plan</th>
            <th>Estado</th>
            <th>Notas</th>
          </tr>
        `;
        tableRowsHtml = reportData.map(item => `
          <tr>
            <td style="white-space: nowrap;">${item.appointment_date || ''}</td>
            <td style="white-space: nowrap; font-weight: bold;">${(item.appointment_time || '').substring(0, 5)} hs</td>
            <td>
              <strong>${item.patient_name || ''}</strong><br/>
              <small style="color: #555;">DNI: ${item.patient_dni || item.study_details?.patient_dni || '---'}</small>
            </td>
            <td>
              <small>${item.patient_phone || '-'}<br/>${item.patient_email || '-'}</small>
            </td>
            <td>${item.professional_name || '---'}</td>
            <td>${item.study_type || 'CONSULTA GENERAL'}</td>
            <td>${item.insurance_provider || item.study_details?.insurance_provider || 'PARTICULAR'} (${item.insurance_plan || item.study_details?.insurance_plan || '-'})</td>
            <td style="text-transform: uppercase; font-weight: bold; font-size: 11px;">${item.status || 'PENDIENTE'}</td>
            <td style="font-size: 11px; color: #444;">${item.admin_notes || item.notes || '-'}</td>
          </tr>
        `).join('');
      }

      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Impresión de Listado - ${tabName}</title>
          <style>
            @page {
              size: A4 landscape;
              margin: 10mm;
            }
            body {
              font-family: Arial, Helvetica, sans-serif;
              color: #000;
              background: #fff;
              margin: 0;
              padding: 15px;
            }
            .header-print {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 2px solid #000;
              padding-bottom: 10px;
              margin-bottom: 12px;
            }
            .header-print h2 {
              margin: 0;
              font-size: 18px;
              text-transform: uppercase;
            }
            .header-print p {
              margin: 3px 0 0 0;
              font-size: 12px;
              color: #444;
            }
            .meta-info {
              font-size: 12px;
              margin-bottom: 12px;
              color: #333;
              display: flex;
              gap: 20px;
              background: #f5f5f5;
              padding: 8px 12px;
              border-radius: 4px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 11px;
            }
            th {
              background-color: #e0e0e0;
              color: #000;
              border: 1px solid #444;
              padding: 6px 8px;
              text-align: left;
              font-weight: bold;
            }
            td {
              border: 1px solid #888;
              padding: 6px 8px;
              vertical-align: top;
            }
            tr:nth-child(even) {
              background-color: #fcfcfc;
            }
            .footer-print {
              margin-top: 15px;
              font-size: 10px;
              color: #666;
              text-align: right;
              border-top: 1px solid #ccc;
              padding-top: 5px;
            }
          </style>
        </head>
        <body>
          <div class="header-print">
            <div>
              <h2>GESTIONAMIENTO DE TURNOS</h2>
              <p>Reporte: <strong>${tabName}</strong></p>
            </div>
            <div style="text-align: right; font-size: 11px; color: #555;">
              Fecha de Emisión: ${new Date().toLocaleDateString('es-AR')} ${new Date().toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>

          <div class="meta-info">
            <span><strong>Filtro Fecha:</strong> ${selectedDate || 'Todas'}</span>
            <span><strong>Profesional/Área:</strong> ${selectedProf || 'Todos'}</span>
            <span><strong>Total Registros:</strong> ${reportData.length}</span>
          </div>

          <table>
            <thead>
              ${tableHeadersHtml}
            </thead>
            <tbody>
              ${tableRowsHtml}
            </tbody>
          </table>

          <div class="footer-print">
            Documento generado por GDT System - Página 1 de 1
          </div>

          <script>
            window.onload = function() {
              window.print();
              setTimeout(function() { window.close(); }, 800);
            };
          </script>
        </body>
        </html>
      `;

      printWindow.document.open();
      printWindow.document.write(htmlContent);
      printWindow.document.close();
    } catch (err) {
      console.error('Error printing list:', err);
      addNotification('Error al abrir la ventana de impresión: ' + err.message, 'error');
    }
  };

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#ffc658'];

  return (
    <div className="reports-page">
      <div className="page-header">
        <div className="title-section">
          <h1>Listados y Auditoría</h1>
          <p>Consulta y exporta el historial completo de la clínica.</p>
        </div>
        <div className="header-actions">
          <button className="btn-secondary" onClick={handleExportExcel}>
            <Download size={18} /> EXCEL
          </button>
          <button className="btn-primary" onClick={handlePrintList}>
            <Printer size={18} /> IMPRIMIR LISTADO
          </button>
        </div>
      </div>

      <div className="reports-main-container card glass-panel-premium">
        {/* Botonera Superior - Estilo Blipdoc Original */}
        <div className="report-tabs-container">
          <div className="tabs-row">
            {reportButtons.slice(0, 6).map(btn => (
              <button 
                key={btn.id} 
                className={`tab-btn-blip ${activeTab === btn.id ? 'active' : ''}`}
                onClick={() => setActiveTab(btn.id)}
              >
                {btn.label}
              </button>
            ))}
          </div>
          <div className="tabs-row second">
            {reportButtons.slice(6).map(btn => (
              <button 
                key={btn.id} 
                className={`tab-btn-blip ${activeTab === btn.id ? 'active' : ''}`}
                onClick={() => setActiveTab(btn.id)}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        <div className="reports-content-area">
          <div className="report-header-flex">
            <h2 className="report-current-title">
              {reportButtons.find(b => b.id === activeTab)?.label}
            </h2>
          </div>
          
          <div className="blip-filter-bar">
            <div className="blip-filter-item">
              <label>Fecha de los Turnos</label>
              <div className="blip-input-group">
                <input 
                  type="date" 
                  value={selectedDate} 
                  onChange={(e) => setSelectedDate(e.target.value)} 
                />
                <CalendarIcon size={16} className="blip-icon" />
              </div>
            </div>

            <div className="blip-filter-item">
              <label>Profesional</label>
              <div className="blip-input-group">
                 <select 
                   value={selectedProf} 
                   onChange={(e) => setSelectedProf(e.target.value)}
                   disabled={!isAdmin || activeTab === 'pacientes-borrados' || activeTab === 'caja-borrados'}
                 >
                   {!isAdmin ? (
                     <option value={staffName}>{staffName}</option>
                   ) : (
                     <>
                       <option value="VER TODOS">-- TODOS LOS PROFESIONALES --</option>
                       {professionals.map(p => <option key={p.id} value={p.full_name || p.name}>{p.full_name || p.name}</option>)}
                     </>
                   )}
                 </select>
                <ChevronDown size={16} className="blip-icon" />
              </div>
            </div>
          </div>

          <div className="report-results-container">
            {activeTab === 'graficos' ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '20px', padding: '20px' }}>
                <div className="card glass-panel" style={{ flex: '1 1 400px', minWidth: 0, padding: '20px' }}>
                  <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', textAlign: 'center' }}>Distribución de Estados</h3>
                  {reportData.length === 0 ? <p className="text-center-p">No hay datos</p> : (
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie data={getPieData()} cx="50%" cy="50%" outerRadius={100} dataKey="value" label>
                          {getPieData().map((entry, index) => <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />)}
                        </Pie>
                        <Tooltip contentStyle={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border)', color: 'var(--text-primary)' }} />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </div>
                <div className="card glass-panel" style={{ flex: '1 1 500px', minWidth: 0, padding: '20px' }}>
                  <h3 style={{ color: 'var(--text-primary)', marginBottom: '20px', textAlign: 'center' }}>Turnos por Profesional</h3>
                  {reportData.length === 0 ? <p className="text-center-p">No hay datos</p> : (
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={getProfData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(255,255,255,0.05)" />
                        <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#aaa' }} />
                        <YAxis tick={{ fontSize: 10, fill: '#aaa' }} />
                        <Tooltip cursor={{ fill: 'rgba(255,255,255,0.02)' }} contentStyle={{ backgroundColor: '#1e1e2d', borderColor: '#333', color: '#fff' }} />
                        <Bar dataKey="turnos" fill="#00E676" radius={[4, 4, 0, 0]} barSize={40} />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            ) : (
            <table className="blip-report-table">
              <thead>
                {['diario', 'otorgados', 'cancelados', 'espera', 'ausentes', 'informes'].includes(activeTab) && (
                  <tr>
                    <th style={{ width: '110px' }}>Fecha</th>
                    <th style={{ width: '80px' }}>Hora</th>
                    <th style={{ width: '220px' }}>Paciente</th>
                    <th style={{ width: '180px' }}>Profesional</th>
                    <th>Estudio</th>
                    <th style={{ width: '130px' }}>Estado</th>
                    <th style={{ width: '140px' }}>Verificación</th>
                    <th style={{ width: '90px' }}>Acciones</th>
                  </tr>
                )}
                {['borrados', 'informes-borrados'].includes(activeTab) && (
                  <tr>
                    <th style={{ width: '110px' }}>Fecha Turno</th>
                    <th style={{ width: '80px' }}>Hora</th>
                    <th style={{ width: '200px' }}>Paciente</th>
                    <th style={{ width: '180px' }}>Profesional</th>
                    <th>Estudio</th>
                    <th style={{ width: '170px' }}>Fecha Borrado</th>
                    <th style={{ width: '180px' }}>Borrado Por</th>
                    <th>Notas / Motivo</th>
                  </tr>
                )}
                {activeTab === 'pacientes-borrados' && (
                  <tr>
                    <th style={{ width: '200px' }}>Paciente</th>
                    <th style={{ width: '120px' }}>DNI</th>
                    <th style={{ width: '140px' }}>Celular</th>
                    <th style={{ width: '200px' }}>Email</th>
                    <th>Obra Social</th>
                    <th style={{ width: '170px' }}>Fecha Borrado</th>
                    <th>Borrado Por</th>
                  </tr>
                )}
                {activeTab === 'caja-borrados' && (
                  <tr>
                    <th>Descripción</th>
                    <th style={{ width: '120px' }}>Monto</th>
                    <th style={{ width: '120px' }}>Tipo</th>
                    <th style={{ width: '150px' }}>Categoría</th>
                    <th style={{ width: '170px' }}>Fecha Borrado</th>
                    <th>Borrado Por</th>
                  </tr>
                )}
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={
                      ['diario', 'otorgados', 'cancelados', 'espera', 'ausentes', 'informes'].includes(activeTab) ? 8 :
                      ['borrados', 'informes-borrados'].includes(activeTab) ? 8 :
                      activeTab === 'pacientes-borrados' ? 7 : 6
                    } className="text-center-p">
                      Cargando registros...
                    </td>
                  </tr>
                ) : error ? (
                  <tr>
                    <td colSpan={
                      ['diario', 'otorgados', 'cancelados', 'espera', 'ausentes', 'informes'].includes(activeTab) ? 8 :
                      ['borrados', 'informes-borrados'].includes(activeTab) ? 8 :
                      activeTab === 'pacientes-borrados' ? 7 : 6
                    } className="error-msg-table">
                      ERROR: {error}
                    </td>
                  </tr>
                ) : reportData.length === 0 ? (
                  <tr>
                    <td colSpan={
                      ['diario', 'otorgados', 'cancelados', 'espera', 'ausentes', 'informes'].includes(activeTab) ? 8 :
                      ['borrados', 'informes-borrados'].includes(activeTab) ? 8 :
                      activeTab === 'pacientes-borrados' ? 7 : 6
                    } className="no-data-msg">
                      No se encontraron registros para mostrar con los filtros seleccionados.
                    </td>
                  </tr>
                ) : (
                  reportData.map(row => {
                    if (['diario', 'otorgados', 'cancelados', 'espera', 'ausentes', 'informes'].includes(activeTab)) {
                      return (
                        <tr key={row.id}>
                          <td>{row.appointment_date}</td>
                          <td className="time-col">{row.appointment_time?.substring(0, 5)} hs</td>
                          <td>
                            <div className="patient-report-cell">
                              <span className="p-name-bold">{row.patient_name}</span>
                              <span className="p-dni-small">DNI: {row.patient_dni || '---'}</span>
                            </div>
                          </td>
                          <td>
                            <span className="p-name-bold">{row.professional_name || 'Sin Profesional'}</span>
                          </td>
                          <td>
                            <div className="study-type-cell">
                              <span className="study-type-text">{row.study_type || 'Consulta General'}</span>
                              {row.study_details?.insurance && <span className="os-badge-small">{row.study_details.insurance}</span>}
                            </div>
                          </td>
                          <td>
                            <StatusDropdown 
                              value={row.status || 'pendiente'} 
                              onChange={() => fetchReportData()} 
                            />
                          </td>
                          <td>
                            {(!row.verification_status || row.verification_status === 'not_verified') && (
                              <button 
                                className="btn-verify-badge" 
                                onClick={() => {
                                  setSelectedAppt(row);
                                  setIsVerifyModalOpen(true);
                                }}
                              >
                                <Mail size={12} /> Solicitar
                              </button>
                            )}
                            {row.verification_status === 'sent' && (
                              <span className="v-status sent">
                                <Mail size={12} /> Enviado
                              </span>
                            )}
                            {row.verification_status === 'confirmed' && (
                              <span className="v-status confirmed">
                                <CheckCircle2 size={12} /> Confirmado
                              </span>
                            )}
                            {row.verification_status === 'rejected' && (
                              <span className="v-status rejected" title={row.verification_notes}>
                                <AlertCircle size={12} /> Falta Info
                              </span>
                            )}
                          </td>
                          <td>
                            <button 
                              className="btn-icon-table" 
                              title="Descargar PDF" 
                              onClick={() => addNotification('Descargando legajo...', 'info')}
                            >
                              <Download size={14} />
                            </button>
                          </td>
                        </tr>
                      );
                    } else if (['borrados', 'informes-borrados'].includes(activeTab)) {
                      return (
                        <tr key={row.id}>
                          <td>{row.appointment_date}</td>
                          <td className="time-col">{row.appointment_time?.substring(0, 5)} hs</td>
                          <td>
                            <div className="patient-report-cell">
                              <span className="p-name-bold">{row.patient_name}</span>
                              <span className="p-dni-small">DNI: {row.patient_dni || '---'}</span>
                            </div>
                          </td>
                          <td>
                            <span className="p-name-bold">{row.professional_name || 'Sin Profesional'}</span>
                          </td>
                          <td>
                            <span className="study-type-text">{row.study_type || 'Consulta General'}</span>
                          </td>
                          <td>{row.deleted_at}</td>
                          <td>{row.deleted_by}</td>
                          <td>
                            <span className="obs-text">{row.notes}</span>
                          </td>
                        </tr>
                      );
                    } else if (activeTab === 'pacientes-borrados') {
                      return (
                        <tr key={row.id}>
                          <td className="p-name-bold">{row.patient_name}</td>
                          <td>{row.patient_dni}</td>
                          <td>{row.patient_phone}</td>
                          <td>{row.patient_email}</td>
                          <td>{row.insurance}</td>
                          <td>{row.deleted_at}</td>
                          <td>{row.deleted_by}</td>
                        </tr>
                      );
                    } else if (activeTab === 'caja-borrados') {
                      return (
                        <tr key={row.id}>
                          <td className="p-name-bold">{row.description}</td>
                          <td className="time-col">${row.amount}</td>
                          <td style={{ textTransform: 'uppercase' }}>{row.type}</td>
                          <td>{row.category}</td>
                          <td>{row.deleted_at}</td>
                          <td>{row.deleted_by}</td>
                        </tr>
                      );
                    }
                    return null;
                  })
                )}
              </tbody>
            </table>
            )}
          </div>
        </div>
      </div>

      <VerificationModal 
        isOpen={isVerifyModalOpen} 
        onClose={() => {
          setIsVerifyModalOpen(false);
          fetchReportData();
        }} 
        appointment={selectedAppt} 
      />
    </div>
  );
};

export default Reports;
